/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';
import * as d3 from 'd3';

import urlPath from 'services/url-path';
import {getNetworkReport} from 'services/qos.service';
import {IAppStore} from 'store/reducers';
import {IQosState} from 'store/reducers/qos';

import {INetworkReport} from 'interfaces/qosProps';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

/**
 * ACTION TYPES
 */
export const NETWORK_THROUGHPUT_REPORT_FETCHING = 'qos::NETWORK_THROUGHPUT_REPORT_FETCHING' as const;
export const NETWORK_THROUGHPUT_REPORT_FETCHING_DONE = 'qos::NETWORK_THROUGHPUT_REPORT_FETCHING_DONE' as const;
export const NETWORK_THROUGHPUT_REPORT_FETCHING_ERROR = 'qos::NETWORK_THROUGHPUT_REPORT_FETCHING_ERROR' as const;

/**
 * SELECTORS
 */
export const qosSelector = ({qos}: IAppStore): IAppStore['qos'] => qos;
export const throughputSelector = ({qos}: IAppStore): IQosState['throughput'] => qos.throughput;

/**
 * INTERFACES
 */
interface IRequestNetworkReport {
  type: typeof NETWORK_THROUGHPUT_REPORT_FETCHING;
  kind: string;
}
interface IReceiveNetworkReport {
  type: typeof NETWORK_THROUGHPUT_REPORT_FETCHING_DONE;
  payload: any; // eslint-disable-line
  kind: string;
}
interface IFailedNetworkReport {
  type: typeof NETWORK_THROUGHPUT_REPORT_FETCHING_ERROR;
  error: null | IPortalError;
  kind: string;
}

export type NetworkReportActionsType = IRequestNetworkReport | IReceiveNetworkReport | IFailedNetworkReport;

export interface INetworkReportActions {
  request: (kind) => NetworkReportActionsType;
  receive: (payload, kind) => NetworkReportActionsType;
  failed: (error: null | IPortalError, kind: string) => NetworkReportActionsType;
}

/**
 * ACTIONS
 */

const networkReportActions: INetworkReportActions = {
  request: kind => ({
    type: NETWORK_THROUGHPUT_REPORT_FETCHING,
    kind
  }),
  receive: (payload, kind) => ({
    type: NETWORK_THROUGHPUT_REPORT_FETCHING_DONE,
    payload,
    kind
  }),
  failed: (error, kind) => ({
    type: NETWORK_THROUGHPUT_REPORT_FETCHING_ERROR,
    error,
    kind
  })
};

export const getNetworkThroughput = (data: INetworkReport) => async(dispatch: Dispatch<NetworkReportActionsType>): Promise<void> => {
  const {
    request,
    receive,
    failed
  } = networkReportActions;

  dispatch(request('throughput'));

  try {
    const networkThroughput = await getNetworkReport({
      networkReport: {
        ...data,
        kind: 'Throughput',
        start: data.start.valueOf(),
        end: data.end.valueOf()
      }
    }, urlPath.networkThroughput);
    const parsedCSV = d3.csvParse(networkThroughput) || [];

    dispatch(receive({data: parsedCSV}, 'throughput'));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError, 'throughput'));

    throw portalError;
  }
};

export type qosActionsType = NetworkReportActionsType;